import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  AcademicCapIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowLeftIcon,
  PlayIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid'
import { Card, Badge, InlineSpinner, Button } from '@/components/ui'
import { lmsApi } from '@/api'

interface QuizQuestion {
  id: number
  text: string
  type: 'single' | 'multiple' | 'true_false'
  answers: { id: number; text: string }[]
}

interface Quiz {
  id: number
  title: string
  description: string
  passing_score: number
  max_attempts: number
  time_limit_minutes: number | null
  questions: QuizQuestion[]
}

interface QuizResult {
  score: number
  passed: boolean
  correct_answers: number
  total_questions: number
}

export default function QuizPage() {
  const { slug, lessonId } = useParams<{ slug: string; lessonId: string }>()
  const navigate = useNavigate()

  const [quiz, setQuiz] = useState<Quiz | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [locked, setLocked] = useState(false)
  const [attemptId, setAttemptId] = useState<number | null>(null)
  const [started, setStarted] = useState(false)
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<Record<number, number | number[]>>({})
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<QuizResult | null>(null)
  const [timeLeft, setTimeLeft] = useState<number | null>(null)

  useEffect(() => {
    if (lessonId) {
      fetchQuiz()
    }
  }, [lessonId])

  // Timer
  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0 || result || !started) return

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev && prev <= 1) {
          handleSubmit()
          return 0
        }
        return prev ? prev - 1 : null
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [timeLeft, result, started])

  const fetchQuiz = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await lmsApi.getQuiz(Number(lessonId))
      setQuiz(data)
    } catch (err: any) {
      console.error('Error fetching quiz:', err)
      const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Errore nel caricamento del quiz'
      setLocked(!!err.response?.data?.locked)
      setError(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleStartQuiz = async () => {
    if (!quiz) return

    try {
      const response = await lmsApi.startQuiz(Number(lessonId))
      setAttemptId(response.attempt_id)
      setStarted(true)
      if (quiz.time_limit_minutes) {
        setTimeLeft(quiz.time_limit_minutes * 60)
      }
    } catch (err: any) {
      console.error('Error starting quiz:', err)
      setError(err.response?.data?.error || 'Errore nell\'avvio del quiz')
    }
  }

  const handleSelectAnswer = (questionId: number, answerId: number, isMultiple: boolean) => {
    setAnswers((prev) => {
      if (isMultiple) {
        const current = (prev[questionId] as number[]) || []
        if (current.includes(answerId)) {
          return { ...prev, [questionId]: current.filter((id) => id !== answerId) }
        } else {
          return { ...prev, [questionId]: [...current, answerId] }
        }
      } else {
        return { ...prev, [questionId]: answerId }
      }
    })
  }

  const handleSubmit = async () => {
    if (!quiz || !attemptId) return

    setSubmitting(true)
    try {
      const response = await lmsApi.submitQuiz(attemptId, answers)
      setResult({
        score: response.score,
        passed: response.passed,
        correct_answers: response.answers?.filter((a) => a.is_correct).length || Math.round((response.score / 100) * quiz.questions.length),
        total_questions: quiz.questions.length,
      })
    } catch (err: any) {
      console.error('Error submitting quiz:', err)
      setError(err.response?.data?.error || 'Errore nell\'invio del quiz')
    } finally {
      setSubmitting(false)
    }
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const isAnswerSelected = (questionId: number, answerId: number): boolean => {
    const answer = answers[questionId]
    if (Array.isArray(answer)) {
      return answer.includes(answerId)
    }
    return answer === answerId
  }

  const getAnsweredCount = () => {
    return Object.keys(answers).length
  }

  if (loading) {
    return <InlineSpinner message="Caricamento quiz..." />
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="p-8 text-center">
          {locked ? (
            <LockClosedIcon className="h-16 w-16 text-amber-400 mx-auto mb-4" />
          ) : (
            <XCircleIcon className="h-16 w-16 text-red-400 mx-auto mb-4" />
          )}
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {locked ? 'Questionario bloccato' : 'Errore'}
          </h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate(`/courses/${slug}/lesson/${lessonId}`)}>
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Torna alla lezione
          </Button>
        </Card>
      </div>
    )
  }

  if (!quiz) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="p-8 text-center">
          <AcademicCapIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Quiz non trovato</h2>
          <p className="text-gray-600 mb-6">Il quiz richiesto non esiste o non e disponibile.</p>
          <Button onClick={() => navigate(`/courses/${slug}/lesson/${lessonId}`)}>
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Torna alla lezione
          </Button>
        </Card>
      </div>
    )
  }

  // Result screen
  if (result) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="p-8 text-center">
          {result.passed ? (
            <CheckCircleSolidIcon className="h-20 w-20 text-green-500 mx-auto mb-4" />
          ) : (
            <XCircleIcon className="h-20 w-20 text-red-500 mx-auto mb-4" />
          )}

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {result.passed ? 'Complimenti!' : 'Quiz non superato'}
          </h2>

          <p className="text-gray-600 mb-6">
            {result.passed
              ? 'Hai superato il quiz con successo!'
              : `Non hai raggiunto il punteggio minimo del ${quiz.passing_score}%`}
          </p>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <div className="text-4xl font-bold text-gray-900 mb-2">{result.score}%</div>
            <div className="text-gray-500">
              {result.correct_answers} / {result.total_questions} risposte corrette
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/courses/${slug}/lesson/${lessonId}`)}>
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Torna alla lezione
            </Button>
            {!result.passed && (
              <Button
                onClick={() => {
                  setResult(null)
                  setAnswers({})
                  setCurrentQuestion(0)
                  setStarted(false)
                  setAttemptId(null)
                  setTimeLeft(null)
                }}
              >
                Riprova
              </Button>
            )}
          </div>
        </Card>
      </div>
    )
  }

  // Start screen
  if (!started) {
    return (
      <div className="max-w-2xl mx-auto py-12">
        <Card className="p-8">
          <div className="text-center mb-8">
            <AcademicCapIcon className="h-16 w-16 text-primary-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{quiz.title}</h1>
            {quiz.description && <p className="text-gray-600">{quiz.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{quiz.questions.length}</div>
              <div className="text-sm text-gray-500">Domande</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{quiz.passing_score}%</div>
              <div className="text-sm text-gray-500">Punteggio minimo</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">
                {quiz.time_limit_minutes ? `${quiz.time_limit_minutes} min` : 'Illimitato'}
              </div>
              <div className="text-sm text-gray-500">Tempo limite</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{quiz.max_attempts}</div>
              <div className="text-sm text-gray-500">Tentativi max</div>
            </div>
          </div>

          <div className="flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate(`/courses/${slug}/lesson/${lessonId}`)}>
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Torna alla lezione
            </Button>
            <Button onClick={handleStartQuiz}>
              <PlayIcon className="h-5 w-5 mr-2" />
              Inizia Quiz
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  // Quiz in progress
  const question = quiz.questions[currentQuestion]
  const isMultiple = question.type === 'multiple'

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{quiz.title}</h1>
        </div>

        {timeLeft !== null && (
          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <ClockIcon className="h-5 w-5" />
            <span className="font-mono font-semibold">{formatTime(timeLeft)}</span>
          </div>
        )}
      </div>

      {/* Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
          <span>
            Domanda {currentQuestion + 1} di {quiz.questions.length}
          </span>
          <span>{getAnsweredCount()} risposte date</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-600 transition-all"
            style={{ width: `${((currentQuestion + 1) / quiz.questions.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Question Card */}
      <Card className="p-6 mb-6">
        <div className="mb-6">
          <Badge variant={isMultiple ? 'warning' : 'default'} size="sm" className="mb-3">
            {question.type === 'single'
              ? 'Risposta singola'
              : question.type === 'multiple'
              ? 'Risposta multipla'
              : 'Vero o Falso'}
          </Badge>
          <h2 className="text-xl font-semibold text-gray-900">{question.text}</h2>
          {isMultiple && (
            <p className="text-sm text-gray-500 mt-2">Seleziona tutte le risposte corrette</p>
          )}
        </div>

        <div className="space-y-3">
          {question.answers.map((answer) => {
            const selected = isAnswerSelected(question.id, answer.id)
            return (
              <button
                key={answer.id}
                onClick={() => handleSelectAnswer(question.id, answer.id, isMultiple)}
                className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all text-left ${
                  selected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div
                  className={`w-6 h-6 flex items-center justify-center ${
                    isMultiple ? 'rounded-md' : 'rounded-full'
                  } border-2 ${
                    selected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                  }`}
                >
                  {selected && <CheckCircleIcon className="h-4 w-4 text-white" />}
                </div>
                <span className={selected ? 'text-primary-700 font-medium' : 'text-gray-700'}>
                  {answer.text}
                </span>
              </button>
            )
          })}
        </div>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentQuestion((prev) => Math.max(0, prev - 1))}
          disabled={currentQuestion === 0}
        >
          Precedente
        </Button>

        <div className="flex gap-2">
          {quiz.questions.map((q, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentQuestion(idx)}
              className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                idx === currentQuestion
                  ? 'bg-primary-600 text-white'
                  : answers[q.id] !== undefined
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {currentQuestion < quiz.questions.length - 1 ? (
          <Button onClick={() => setCurrentQuestion((prev) => prev + 1)}>Successiva</Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting || getAnsweredCount() < quiz.questions.length}
          >
            {submitting ? 'Invio...' : 'Invia Quiz'}
          </Button>
        )}
      </div>

      {/* Warning if not all answered */}
      {currentQuestion === quiz.questions.length - 1 &&
        getAnsweredCount() < quiz.questions.length && (
          <p className="text-center text-amber-600 mt-4">
            Rispondi a tutte le domande prima di inviare il quiz
          </p>
        )}
    </div>
  )
}
